import utilsService from '@/app/helpers/utilsService';
import fetchWrapper from '../fetchWrapper';
import {
    DataPaymentRequest,
    MethodRequest,
    PaymentMethodDesktopResponse,
    PaymentMethodMobileResponse,
    PaymentRequest,
    PaymentRequestMobile,
    PaymentResponseAfterCallBack,
    PaymentResponseAfterCallForMobileResponse,
    PaymentResponseAfterCallResponse,
    PaymentSubmitWorkResponse,
    PaymentVerificationRequest,
} from '@/app/models/payments';
import { PaginatedSubmissionPaymentResponse } from '@/app/models/submission';

const BASE_URL = process.env.BASE_URL;

interface ISubmissionNeedToPayRequest {
    status: number;
    challengeId: number | string;
    submissionId: number | string;
}

interface IPaymentOrderRequest {
    platform: 'mobile' | 'desktop';
    method: string;
    products: Array<{ id: number; qty: number; product_option_id?: number }>;
    addressId?: number; // 전자책만 담긴 주문은 배송지가 없다
    // 함께 쓸 포인트. 상한·잔액은 서버가 다시 검사한다(StorePointService).
    pointUse?: number;
    // 쓸 쿠폰. 소유·만료·할인액은 서버가 다시 계산한다(CouponService).
    couponId?: number;
    // e-book 환불 안내에 동의했는지. e-book 이 없는 주문엔 보내지 않는다.
    ebookRefundConsent?: boolean;
}

async function checkPaid(challengeId: number | string): Promise<PaymentSubmitWorkResponse> {
    const basePath = `challenges/${challengeId}/paid`;
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/${basePath}`);
        const camelizedResponse = utilsService.toCamelCase(response, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

async function getSubmissionNeedToPay({ status, challengeId, submissionId }: ISubmissionNeedToPayRequest): Promise<PaginatedSubmissionPaymentResponse> {
    const basePath = `submissions/pay?status=${status}&challenge_id=${challengeId}&submission_id=${submissionId}`;
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/${basePath}`);
        const camelizedResponse = utilsService.toCamelCase(response, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

async function getMethod({ platform }: MethodRequest): Promise<PaymentMethodMobileResponse | PaymentMethodDesktopResponse> {
    const basePath = `payments/methods?platform=${platform}`;
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/${basePath}`);
        const camelizedResponse = utilsService.toCamelCase(response, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

async function postActionPaymentSubmission({ platform, challengeId, method, submissionId }: DataPaymentRequest): Promise<PaymentResponseAfterCallResponse | PaymentResponseAfterCallForMobileResponse> {
    const basePath = `payments/submission`;
    try {
        const response = await fetchWrapper.post(`${BASE_URL}/${basePath}`, await utilsService.appendToFormData({ platform, challengeId, method, submissionId }));
        const camelizedResponse = utilsService.toCamelCase(response?.data, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

async function postActionPaymentOrder({ platform, method, products, addressId, pointUse, couponId, ebookRefundConsent }: IPaymentOrderRequest): Promise<any> {
    const basePath = `payments/order`;
    try {
        const formDataObj: any = {
            platform,
            method,
            address_id: addressId
        };
        if (pointUse && pointUse > 0) {
            formDataObj.point_use = pointUse;
        }
        if (couponId && couponId > 0) {
            formDataObj.coupon_id = couponId;
        }
        // 전자책이 담긴 주문의 환불 안내 동의(서버가 전자책이 있을 때만 필수로 본다).
        if (ebookRefundConsent) {
            formDataObj.ebook_refund_consent = 1;
        }

        products.forEach((product, index) => {
            formDataObj[`products[${index}][id]`] = product.id;
            formDataObj[`products[${index}][qty]`] = product.qty;
            if (product.product_option_id != null) {
                formDataObj[`products[${index}][product_option_id]`] = product.product_option_id;
            }
        });

        const response = await fetchWrapper.post(`${BASE_URL}/${basePath}`, await utilsService.appendToFormData(formDataObj));
        const camelizedResponse = utilsService.toCamelCase(response?.data, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

function parseFormData(textData: string): { [key: string]: string } {
    const params = new URLSearchParams(textData);
    const data: { [key: string]: string } = {};
    params.forEach((value, key) => {
        data[key] = value;
    });
    return data;
}

async function postVerification({ data }: PaymentVerificationRequest): Promise<PaymentResponseAfterCallBack> {
    const basePath = `payments/callback`;
    try {
        const response = await fetch(`${BASE_URL}/${basePath}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData = await response.json();

        const camelizedResponse = utilsService.toCamelCase(responseData, { deep: true });

        return camelizedResponse;
    } catch (error) {
        console.error('Error in postVerification:', error);
        throw error;
    }
}

async function getOrderDetail(orderCode: string): Promise<any> {
    const basePath = `payments/order/${orderCode}`;
    try {
        const response = await fetchWrapper.get(`${BASE_URL}/${basePath}`);
        const camelizedResponse = utilsService.toCamelCase(response?.data, { deep: true });
        return camelizedResponse;
    } catch (error) {
        throw error;
    }
}

async function postCallbackLog(logData: {
    platform: 'mobile' | 'desktop' | 'app_webview' | 'unknown';
    userAgent: string;
    data: { [key: string]: string };
    timestamp: string;
}): Promise<any> {
    const basePath = `payments/callback/logs`;
    try {
        const response = await fetch(`${BASE_URL}/${basePath}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(logData),
        });

        if (!response.ok) {
            console.warn(`Failed to log callback: ${response.status}`);
            return;
        }

        const responseData = await response.json();
        return responseData;
    } catch (error) {
        console.error('Error logging callback:', error);
        // Don't throw - logging should not interrupt the payment flow
    }
}

async function getOrderByOrderCode(orderCode: string): Promise<any> {
    const basePath = `orders/${orderCode}`;
    try {
        const response = await fetch(`${BASE_URL}/${basePath}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData = await response.json();
        const camelizedResponse = utilsService.toCamelCase(responseData, { deep: true });
        return camelizedResponse;
    } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
    }
}

async function patchOrderNoteByOrderCode(orderCode: string, note: string): Promise<any> {
    const basePath = `orders/update-note`;
    try {
        const response = await fetchWrapper.patch(`${BASE_URL}/${basePath}`, {
            order_code: orderCode,
            note,
        });

        const camelizedResponse = utilsService.toCamelCase(response?.data, { deep: true });
        return camelizedResponse;
    } catch (error) {
        console.error('Error updating order note:', error);
        throw error;
    }
}

// return `${process.env.BASE_URL}/payments/callback`;

export const paymentService = {
    checkPaid,
    getSubmissionNeedToPay,
    getMethod,
    postActionPaymentSubmission,
    postActionPaymentOrder,
    postVerification,
    getOrderDetail,
    getOrderByOrderCode,
    patchOrderNoteByOrderCode,
    postCallbackLog,
};
